import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as InfoIcon } from "../../../../../../assets/fi-rr-info.svg";
import CustomDate from "../../../../../../components/customDate/CustomDate";
import CustomSelect from "../../../../../../components/customSelect/CustomSelect";
import CustomInput from "../../../../../../components/customInput/CustomInput";
import { Group, Radio, Paper, Modal } from "@mantine/core";
import { useForm, Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import AsyncSelect from "react-select/async";
import CustomDateAndTime from "../../../../../../components/customDateAndTime/CustomDateAndTime";
import CustomDOBPicker from "../../../../../../components/CustomDOBPicker";
import CustomButton from "../../../../../../components/CustomButton";
import { ReactComponent as ClockIcon } from "../../../../../../assets/clock.svg"
import moment from "moment";
import { useMediaQuery } from "@mantine/hooks";
import RHFRadioGroup from "../../../../../../components/form/RHFRadioGroup";
import RHFTextInput from "../../../../../../components/form/RHFTextInput";
import RHFCustomDatePicker from "../../../../../../components/form/RHFCustomDatePicker";
import RHFCheckbox from "../../../../../../components/form/RHFCheckbox";
import { calculateDelayDuration, dateFormat, fetchAirlines, fetchAirports, isValidTime, validateDateFormat } from "../../../../../../components/utils/flightApiUtils";
import Swal from "sweetalert2";
import axios from "axios";
import base_url from "../../../../../../api/bootapi";
import RHFAsyncFlightSelect from "../../../../../../components/form/RHFAsyncFlightSelect";
import RHFSelect from "../../../../../../components/form/RHFSelect";
import RHFTimeInput from "../../../../../../components/form/RHFTimeInput";
import { RemoveCircleOutline } from "@mui/icons-material";


const FlightItem = ({ index, remove, claimType, defaultAirlines, defaultAirports, openFlightModal, setLoading }) => {

  const { watch, setValue, control, formState: { errors } } = useFormContext();

  // const [loading, setLoading] = useState(false)


  const loadAirlineOptions = (inputValue, search_by) => {
    return fetchAirlines(inputValue, search_by); // Just return the promise
  };

  const loadOptions = (inputValue) => {
    return fetchAirports(inputValue); // Just return the promise
  };


  // Watch fields needed for conditional logic
  const searchBy = watch(`actual_flight_details.${index}.search_by`) || "airline_name";
  const isManual = watch(`actual_flight_details.${index}.is_manual`)

  // Fetch the original flight data for this index to power the "Same as Original" feature
  const originalFlight = watch(`original_flight_details.${index}`);



  const handleSameAsOriginalToggle = (isChecked) => {

    const options = {
      shouldDirty: true,
      shouldValidate: true,
    };


    setValue(`actual_flight_details.${index}.same_as_original`, isChecked);

    if (isChecked && originalFlight) {
      // Sync original booking data into the actual flight form state
      setValue(`actual_flight_details.${index}.actual_airline`, originalFlight.airline, options);
      setValue(`actual_flight_details.${index}.actual_hub_code`, originalFlight.hub_code, options);
      setValue(`actual_flight_details.${index}.actual_flight_number`, originalFlight.flight_number, options);
      setValue(`actual_flight_details.${index}.actual_date_of_travel`, originalFlight.date_of_travel, options);
      setValue(`actual_flight_details.${index}.actual_flight_class`, originalFlight.flight_class, options);
      setValue(`actual_flight_details.${index}.actual_airport_of_origin`, originalFlight.airport_of_origin, options);
      setValue(`actual_flight_details.${index}.actual_airport_of_destination`, originalFlight.airport_of_destination, options);
      setValue(`actual_flight_details.${index}.departure_iata_code`, originalFlight.departure_iata_code, options);
      setValue(`actual_flight_details.${index}.arrival_iata_code`, originalFlight.arrival_iata_code, options);
      setValue(`actual_flight_details.${index}.actual_departure_time`, originalFlight.actual_departure_time, options);
      setValue(`actual_flight_details.${index}.actual_departure_date`, originalFlight.actual_departure_date, options);
      setValue(`actual_flight_details.${index}.actual_arrival_time`, originalFlight.actual_arrival_time, options);
      setValue(`actual_flight_details.${index}.actual_arrival_date`, originalFlight.actual_arrival_date, options);

    } else {
      // Clear data if unchecked
      setValue(`actual_flight_details.${index}.actual_airline`, "", options);
      setValue(`actual_flight_details.${index}.actual_flight_number`, "", options);
      setValue(`actual_flight_details.${index}.actual_date_of_travel`, null, options);
      setValue(`actual_flight_details.${index}.actual_flight_class`, "Economy", options);
      setValue(`actual_flight_details.${index}.actual_airport_of_origin`, "", options);
      setValue(`actual_flight_details.${index}.actual_airport_of_destination`, "", options);
      setValue(`actual_flight_details.${index}.departure_iata_code`, "", options);
      setValue(`actual_flight_details.${index}.arrival_iata_code`, "", options);
      setValue(`actual_flight_details.${index}.actual_departure_time`, "", options);
      setValue(`actual_flight_details.${index}.actual_departure_date`, "", options);
      setValue(`actual_flight_details.${index}.actual_arrival_time`, "", options);
      setValue(`actual_flight_details.${index}.actual_arrival_date`, "", options);

    }
  };



  const flight = useWatch({
    control,
    name: `actual_flight_details.${index}`
  });

  const fetchFlightDetails = async () => {
    setLoading(true);

    try {
      const response = await axios.post(
        `${base_url.api1}/tourapp/flight_states`,
        {
          airline_code: flight?.actual_hub_code,
          flight_number: flight.actual_flight_number,
          flight_date: moment(flight.actual_date_of_travel).format("YYYY-MM-DD"),
        }
      );

      const flightOptionsData = response?.data?.Flight?.map((flightData) => {
        const departureScheduled = flightData.departure.scheduled;
        const departureActual = flightData.departure.actual;
        const departureActualRunway = flightData.departure.actual_runway;

        const arrivalScheduled = flightData.arrival.scheduled;
        // console.log(arrivalScheduled)
        const arrivalActual = flightData.arrival.actual;
        const arrivalActualRunway = flightData.arrival.actual_runway;

        const delayDuration = calculateDelayDuration(arrivalScheduled, arrivalActual || arrivalActualRunway);
        return {
          value: `${flightData.departure.iata}-${flightData.arrival.iata}`,
          departure: {
            airport: flightData.departure.airport,
            iata: flightData.departure.iata,
            departureScheduled,
            timeZone: flightData.departure.timezone,
            departureActual: departureActual || departureActualRunway,
          },
          arrival: {
            airport: flightData.arrival.airport,
            iata: flightData.arrival.iata,
            arrivalScheduled,
            timeZone: flightData.arrival.timezone,

            arrivalActual: arrivalActual || arrivalActualRunway,
          },
          delay_cal: delayDuration,
          label: `${flightData.arrival.airport} - ${flightData.departure.airport}`,
        };
      });

      const flights = response.data.Flight;

      if (!flights.length) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: `Flight not found`,
        })
        resetFlight();

        return;
      }

      if (flights.length > 1) {
        openFlightModal(flights, `actual_flight_details.${index}`, flightOptionsData);
        return;
      }

      updateFlight(flightOptionsData[0]);

    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Flight not found`,
      })
      resetFlight()

      // console.log(e);
    } finally {
      setLoading(false);

    }
  };


  const resetFlight = () => {
    setValue(
      `actual_flight_details.${index}.actual_airport_of_origin`,
      ""
    );

    setValue(
      `actual_flight_details.${index}.actual_airport_of_destination`,
      ""
    );

    setValue(
      `actual_flight_details.${index}.actual_departure_time`,
      ""
    );

    setValue(
      `actual_flight_details.${index}.actual_arrival_time`,
      ""
    );

    setValue(
      `actual_flight_details.${index}.actual_departure_date`,
      ""
    );

    setValue(
      `actual_flight_details.${index}.actual_arrival_date`,
      ""
    );

    setValue(
      `actual_flight_details.${index}.departure_iata_code`,
      ''
    );

    setValue(
      `actual_flight_details.${index}.arrival_iata_code`,
      ''
    );
  }

  const updateFlight = (flight) => {
    // console.log(flight)
    // Extract values to make the code easier to read
    const depActual = flight?.departure?.departureActual;
    const depTz = flight?.departure?.timeZone;

    const arrActual = flight?.arrival?.arrivalActual;
    const arrTz = flight?.arrival?.timeZone;

    // 2. Set the standard text fields (with fallback to empty string)
    setValue(`actual_flight_details.${index}.actual_airport_of_origin`, flight?.departure?.airport || "");
    setValue(`actual_flight_details.${index}.actual_airport_of_destination`, flight?.arrival?.airport || "");
    setValue(`actual_flight_details.${index}.departure_iata_code`, flight?.departure?.iata || "");
    setValue(`actual_flight_details.${index}.arrival_iata_code`, flight?.arrival?.iata || "");

    // Set delay if it exists in your flight object
    if (flight?.delay_cal !== undefined) {
      setValue(`actual_flight_details.${index}.delay_cal`, flight.delay_cal);
    }

    // 3. Conditionally set Departure Date & Time
    if (isValidTime(depActual)) {
      const formattedDep = dateFormat(depActual, depTz);
      setValue(`actual_flight_details.${index}.actual_departure_time`, formattedDep.time);
      setValue(`actual_flight_details.${index}.actual_departure_date`, formattedDep.date);
    } else {
      setValue(`actual_flight_details.${index}.actual_departure_time`, "");
      setValue(`actual_flight_details.${index}.actual_departure_date`, "");
    }

    // 4. Conditionally set Arrival Date & Time
    if (isValidTime(arrActual)) {
      const formattedArr = dateFormat(arrActual, arrTz);
      setValue(`actual_flight_details.${index}.actual_arrival_time`, formattedArr.time);
      setValue(`actual_flight_details.${index}.actual_arrival_date`, formattedArr.date);
    } else {
      setValue(`actual_flight_details.${index}.actual_arrival_time`, "");
      setValue(`actual_flight_details.${index}.actual_arrival_date`, "");
    }
  };

  const isValidDate = (str) => {
    return moment(str, "MM-DD-YYYY", true).isValid();
  };
  useEffect(() => {
    if (
      flight?.actual_date_of_travel &&
      isValidDate(flight?.actual_date_of_travel) &&
      flight?.actual_airline &&
      flight?.actual_hub_code &&
      flight?.triggered &&

      flight?.actual_flight_number
    ) {
      fetchFlightDetails();
    }
  }, [
    flight?.actual_date_of_travel,
    flight?.actual_airline,
    flight?.actual_flight_number,
    flight?.actual_hub_code,
    flight?.triggered

  ]);

  return (
    <div key={index}>
      <div className="flight-add-wrapper">
        <h3>Flight {index + 1}</h3>
        {index > 0 && (
          <div
            style={{
              backgroundColor: "none",
              color: "red",
              cursor: "pointer",
              marginLeft: "2px",
              textAlign: "right",
            }}
            onClick={() => remove(index)}
          >
            <RemoveCircleOutline />
          </div>
        )}
      </div>


      {
        claimType !== "lost/damaged" && claimType !== "lost_wages" && <>
          <h4
            className="stepper-sub-header"
            style={{ marginBottom: "5px" }}
          >
            How was this flight provided?{" "}
            <span style={{ color: "#de8385" }}> *</span>
          </h4>


          <Group mb="sm" align="center">
            <RHFRadioGroup
              name={`actual_flight_details.${index}.booking_method`}
              orientation="horizontal"
              rules={{ required: "Please select provider" }}
              options={[
                { label: "Booked this flight myself", value: "self_booked" },
                { label: "Airline rebooked me", value: "airline" },

              ]}
            />
          </Group>




        </>
      }



      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "35px",
          marginTop: "30px"
        }}
      >


        <div className="file-a-claim">
          <h4
            className="stepper-sub-header"
            style={{ marginBottom: "30px" }}
          >
            Provide details of the flights you actually took.
          </h4>


          {originalFlight && claimType !== "lost/damaged" && claimType !== "lost_wages" && (

            <RHFCheckbox
              name={`actual_flight_details.${index}.same_as_original`}
              label="Same as Original Flight"
              onValueChange={handleSameAsOriginalToggle}
              mb='30px'
            />

          )}


          <div className="claim-grid" style={{ marginBottom: '20px' }}>
            <div className="custom-input" >

              <label className="input-label">
                Search by

              </label>

              <Controller
                name={`actual_flight_details.${index}.search_by`}
                control={control}
                render={({ field }) => (
                  <Radio.Group
                    {...field}
                  >
                    <div className="claim-form-radio-wrapper">

                      <Radio
                        color="red"
                        variant="outline"
                        value={"airline_name"}
                        label="Airline name"
                        size="xs"
                      />

                      <Radio
                        color="red"
                        variant="outline"
                        value={"iata_code"}
                        label="Airline code"
                        size="xs"

                      />
                    </div>
                  </Radio.Group>
                )}
              />



            </div>





          </div>
          <div className="claim-grid">
            <RHFAsyncFlightSelect
              name={`actual_flight_details.${index}.actual_airline`}
              label="Airline"
              withAsterisk
              rules={{ required: "Airline is required" }}
              defaultOptions={defaultAirlines}
              loadOptions={(val) => loadAirlineOptions(val, searchBy)}
              onChangeCallback={(selectedOption) => {

                setValue(`actual_flight_details.${index}.triggered`, true);

                setValue(`actual_flight_details.${index}.actual_hub_code`, selectedOption?.hub_code);


              }}
            />

            <RHFTextInput
              name={`actual_flight_details.${index}.actual_flight_number`}
              label="Flight Number"
              placeholder='Enter flight number'
              withAsterisk
              maxLength={10}
              rules={{
                required: "Flight Number is Required",
                pattern: { value: /^[a-zA-Z0-9]+$/, message: "Only alphanumeric characters allowed" }
              }}
              onChange={(e) => {
                setValue(`actual_flight_details.${index}.triggered`, true);

                setValue(`actual_flight_details.${index}.actual_flight_number`, e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase());
              }}
            />


            <div className="new-claim-date-time-wrapper">
              <label className="custom-date-time-label" style={{ display: 'block', marginBottom: '8px' }}>
                Scheduled departure date & time <span style={{ color: "#de8385" }}>*</span>
              </label>

              <div style={{ position: "relative" }}>

                <div
                  className="combined-datetime-pill"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#e8eef7", // Matches the light grayish-blue in the image
                    borderRadius: "999px",      // Creates the pill shape
                    padding: "0px 16px",
                    width: "100%",
                    border: "1px solid #e8eef7", // Add a subtle border on focus if desired later
                  }}
                >
                  <RHFCustomDatePicker
                    name={`actual_flight_details.${index}.actual_date_of_travel`}
                    rules={{
                      required: "Travel Date is required",
                      validate: validateDateFormat, // Ensure you have this function defined
                    }}

                    placeholder="Choose date"
                    hideError={true}

                    // Mantine prop to remove borders and background
                    variant="unstyled"

                    // Override styles to ensure proper width and spacing
                    styles={{
                      input: {
                        flex: 1,

                      },
                    }}
                    onValueChange={(value) => {
                      setValue(`actual_flight_details.${index}.triggered`, true);
                    }}
                  />

                  {/* 
        The calendar icon from RHFCustomDatePicker naturally sits between 
        these two inputs because it's set as the rightSection! 
      */}

                  <RHFTimeInput
                    name={`actual_flight_details.${index}.actual_departure_time`}
                    rules={{ required: "Departure Time is required" }}

                    placeholder="Time"
                    hideError={true}
                    readOnly={!isManual}




                    // Mantine prop to remove borders and background
                    variant="unstyled"

                    styles={{
                      input: {
                        flex: 1,

                      }
                    }}

                  />
                </div>
              </div>

              {/* Error Message Display */}
              {(errors.actual_flight_details?.[index]?.actual_date_of_travel ||
                errors.actual_flight_details?.[index]?.actual_departure_time) && (
                  <p className="errormessage" style={{ color: "red", fontSize: "12px" }}>
                    {errors.actual_flight_details?.[index]?.actual_date_of_travel?.message ||
                      errors.actual_flight_details?.[index]?.actual_departure_time?.message ||
                      "Travel Date and Time is Required"}
                  </p>
                )}
            </div>

            <RHFAsyncFlightSelect
              name={`actual_flight_details.${index}.actual_airport_of_origin`}
              label="Airport of Origin"
              withAsterisk

              isDisabled={!isManual}

              defaultOptions={defaultAirports} // Pass the hoisted data here
              loadOptions={loadOptions}
              rules={{ required: "Origin airport is required" }}
            />


            <div className="new-claim-date-time-wrapper">
              <label className="custom-date-time-label" style={{ display: 'block', marginBottom: '8px' }}>
                Scheduled arrival date & time <span style={{ color: "#de8385" }}>*</span>
              </label>

              <div style={{ position: "relative" }}>

                <div
                  className="combined-datetime-pill"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#e8eef7", // Matches the light grayish-blue in the image
                    borderRadius: "999px",      // Creates the pill shape
                    padding: "0px 16px",
                    width: "100%",
                    border: "1px solid #e8eef7", // Add a subtle border on focus if desired later
                  }}
                >
                  <RHFCustomDatePicker
                    name={`actual_flight_details.${index}.actual_arrival_date`}
                    rules={{
                      required: "Arrival Date is required",
                      validate: validateDateFormat, // Ensure you have this function defined
                    }}

                    placeholder="Choose date"
                    hideError={true}
                    readOnly={!isManual}
                    hidePop={!isManual}


                    // Mantine prop to remove borders and background
                    variant="unstyled"

                    // Override styles to ensure proper width and spacing
                    styles={{
                      input: {
                        flex: 1,

                      },
                    }}

                  />

                  {/* 
        The calendar icon from RHFCustomDatePicker naturally sits between 
        these two inputs because it's set as the rightSection! 
      */}

                  <RHFTimeInput
                    name={`actual_flight_details.${index}.actual_arrival_time`}
                    rules={{ required: "Arrival Time is required" }}

                    placeholder="Time"
                    hideError={true}

                    readOnly={!isManual}

                    // Mantine prop to remove borders and background
                    variant="unstyled"

                    styles={{
                      input: {
                        flex: 1,

                      }
                    }}

                  />
                </div>
              </div>

              {/* Error Message Display */}
              {(errors.actual_flight_details?.[index]?.actual_arrival_date ||
                errors.actual_flight_details?.[index]?.actual_arrival_time) && (
                  <p className="errormessage" style={{ color: "red", fontSize: "12px" }}>
                    {errors.actual_flight_details?.[index]?.actual_arrival_date?.message ||
                      errors.actual_flight_details?.[index]?.actual_arrival_time?.message ||
                      "Arrival Date and Time is Required"}
                  </p>
                )}
            </div>



            <RHFAsyncFlightSelect
              name={`actual_flight_details.${index}.actual_airport_of_destination`}
              label="Airport of Destination"
              withAsterisk
              isDisabled={!isManual}


              defaultOptions={defaultAirports} // Pass the hoisted data here
              loadOptions={loadOptions}
              rules={{ required: "Destination airport is required" }}
            />

            <RHFSelect
              name={`actual_flight_details.${index}.actual_flight_class`}
              label="Flight Class"
              withAsterisk
              data={[
                { label: "Economy", value: "Economy" },
                { label: "Business", value: "Business" },
                { label: "First Class", value: "First Class" }
              ]}
              rules={{ required: "Flight Class is required" }}
            />

            <div className="custom-input">

              <label className="input-label">Flight info didn't appear ?</label>
              <button className="btn btn-manual" type="button" onClick={() => {
                setValue(`actual_flight_details.${index}.is_manual`, true, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }} >

                enter manually

              </button>

            </div>




          </div>

        </div>


      </div>





    </div>
  )
}



const ActualFlightTaken = ({
  defaultAirlines, defaultAirports, openFlightModal, setLoading, setActive, setTemporaryOpened
}) => {

  const isMobile = useMediaQuery("(max-width: 768px)");
  const [connectingFlightModalOpened, setConnectingFlightOpened] = useState(false)
  const { control, watch, setValue, formState: { errors }, trigger } = useFormContext();
  const type_of_claim = watch('type_of_claim');
  const required_actual_flight = watch('required_actual_flight');

  const handleBack = () => {
    if (type_of_claim === "lost/damaged" || type_of_claim === "lost_wages") {
      setActive(c => c - 2);
    } else {
      setActive(c => c - 1);

    }
  }


  const handleNext = async () => {
    const isValid = await trigger([
      "actual_flight_details",
    ]);

    if (!isValid) {
      return;
    }

    setConnectingFlightOpened(true);

  }

  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "actual_flight_details",
  });


  return (
    <div className="stepper-container">
      <div className="stepper-header-container">
        <h3 className="stepper-header" style={{ margin: 0 }}>
          {
            (type_of_claim === "lost/damaged" || type_of_claim === "lost_wages") ? "Flight details" : "Actual flights taken"
          }
        </h3>

      </div>

      {
        type_of_claim !== "lost/damaged" && type_of_claim !== "lost_wages" && <div>
          <h4 className="stepper-sub-header" style={{ marginBottom: "5px" }}>
            Did you take a different flight than originally booked? <span style={{ color: "#de8385" }}>*</span>
          </h4>
          <Group mb="sm" align="center">
            <RHFRadioGroup
              name={`required_actual_flight`}
              orientation="horizontal"
              rules={{ required: "Please select" }}
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },

              ]}
            />
          </Group>

        </div>
      }




      {required_actual_flight === "yes" && (


        <>


          {
            (type_of_claim === "lost/damaged" || type_of_claim === "lost_wages") && <>
              <h4
                className="stepper-sub-header"
                style={{ marginBottom: "15px" }}
              >
                Estimated value of the claim?
                <span style={{ color: "#de8385" }}> *</span>
              </h4>

              <div className="grid-two">
                <RHFTextInput
                  name="claim_amount"
                  label="Claim Amount"
                  placeholder='Enter claim amount'
                  withAsterisk
                  rules={{ required: "Claim Amount is required" }}
                />
                {
                  type_of_claim === "lost/damaged" && (

                    <RHFCustomDatePicker
                      name={`luggage_delivered`}
                      placeholder="Choose date"
                      withAsterisk
                      label="Luggage Delivered Date"
                      rules={{
                        required: "Luggage Delivered Date is required",
                        validate: validateDateFormat,
                      }}

                    />
                  )

                }


              </div>



            </>
          }





          {fields.length > 0 &&
            fields.map((flight, index) => {


              return <FlightItem key={index}
                index={index}
                remove={remove}
                claimType={type_of_claim}
                defaultAirlines={defaultAirlines}
                defaultAirports={defaultAirports}
                openFlightModal={openFlightModal}
                setLoading={setLoading}
              />

            })}

        </>
      )}


      <div className="stepper-btn-wrapper">
        <button className="secondary-btn new-back-btn" type="button" onClick={handleBack} >
          Back
        </button>

        <div className="add-btn-wrapper" >

          <button className="secondary-btn" type="button" onClick={() => setTemporaryOpened(true)}>
            Temporary Save
          </button>

          <button onClick={handleNext} className="primary-btn" type="button">
            Next
          </button>




        </div>
      </div>

      <Modal

        opened={connectingFlightModalOpened}
        onClose={() => { setConnectingFlightOpened(false); }}

        closeOnClickOutside={false}
        centered
        radius={isMobile ? "lg" : "xl"}
        size={"md"}

      >
        <div style={{ textAlign: "center", padding: "0px 0px 30px 0px" }}>

          {/* <h2 className="">No flight data found</h2> */}
          <p style={{ fontSize: '18px', color: 'red' }}>
            If the flight effected was part of a connecting flight journey, please enter the details of both flights in the proper sequence
          </p>

          <div className="flight-modal-btn-wrapper">
            <button type="button" className="secondary-btn" onClick={() => { append({ search_by: "airline_name", flight_class: 'Economy' }); setConnectingFlightOpened(false) }}  >Additional Flight</button>

            <button type="button" className="primary-btn" onClick={() => {
              setActive(c => c + 1);
              setConnectingFlightOpened(false);

            }} >Next</button>
          </div>






        </div>
      </Modal>
    </div>
  );
};

export default ActualFlightTaken;
